import { useState, useMemo } from 'react'
import { Card, Title, SegmentedControl, Text, Group } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { DatePickerInput } from '@mantine/dates'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'
import { chooseFormat, formatDateForChart } from '@/shared/lib/utils'
import { endOfDay } from 'date-fns'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { CHART_COLORS, ChartGradient } from '@/shared/lib/chartColors.tsx'
import { UsersSessionsHistory } from '@/shared/api/topli/TopliApi'

interface ChartDataPoint {
  date: string
  originalDate: string
  value: number
}

interface DAUChartProps {
  data: UsersSessionsHistory[]
  isLoading?: boolean
}

type PeriodType = 'week' | 'month' | 'custom'

interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

export const DAUChart = ({ data, isLoading }: DAUChartProps) => {
  const { t } = useTranslation(['dashboard'])
  const [periodType, setPeriodType] = useState<PeriodType>('week')
  const [customDateRange, setCustomDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  })
  const { language } = useLanguageStore()

  const fullChartData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data.map((item: UsersSessionsHistory): ChartDataPoint => {
      return {
        date: formatDateForChart(item.date, language, { showDay: true }),
        originalDate: item.date,
        value: item.numberOfSession,
      }
    })
  }, [data, language])

  const chartData = useMemo(() => {
    if (periodType === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      const startDate = customDateRange.startDate
      const endDate = endOfDay(customDateRange.endDate)

      return fullChartData.filter(item => {
        const itemDate = new Date(item.originalDate)
        return itemDate >= startDate && itemDate <= endDate
      })
    } else if (periodType === 'week') {
      return fullChartData.slice(-7)
    } else if (periodType === 'month') {
      return fullChartData.slice(-30)
    }

    return fullChartData.slice(-7)
  }, [fullChartData, periodType, customDateRange])

  const handlePeriodChange = (value: string) => {
    const newPeriodType = value as PeriodType
    setPeriodType(newPeriodType)

    if (newPeriodType !== 'custom') {
      setCustomDateRange({ startDate: null, endDate: null })
    }
  }

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    setCustomDateRange({
      startDate: value[0],
      endDate: value[1],
    })

    if (value[0] && value[1]) {
      setPeriodType('custom')
    }
  }

  const totalUsers = chartData.reduce((sum, item) => sum + item.value, 0)

  if (isLoading) {
    return (
      <Card shadow='sm' padding='lg' radius='md' className='rounded-[1.25rem]'>
        <Title order={3} mb='md'>
          {t('dashboard:dau_chart_title')}
        </Title>
        <div className='h-64 bg-gray-100 animate-pulse rounded'></div>
      </Card>
    )
  }

  return (
    <Card shadow='sm' padding='lg' radius='md' className='rounded-[1.25rem]'>
      <Title order={3} mb='md'>
        {t('dashboard:dau_chart_title')}
      </Title>

      <div className='flex flex-row items-center space-x-2 mb-md'>
        <SegmentedControl
          value={periodType}
          onChange={handlePeriodChange}
          data={[
            { label: t('dashboard:week'), value: 'week' },
            { label: t('dashboard:month'), value: 'month' },
          ]}
        />
        <DatePickerInput
          type='range'
          placeholder={t('dashboard:select_period')}
          value={[customDateRange.startDate, customDateRange.endDate]}
          onChange={handleDateRangeChange}
          clearable
          valueFormat={chooseFormat(language)}
          size='sm'
          className='w-64'
        />
      </div>
      <div className='mb-6' style={{ marginTop: '30px' }}>
        <div>
          <Text size='lg' fw={700}>
            {totalUsers}
          </Text>
          <Text size='sm' c='dimmed'>
            {t('dashboard:total_users')}
          </Text>
        </div>
      </div>

      <div className='h-80'>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={chartData}>
            <defs>
              <ChartGradient />
            </defs>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis
              dataKey='date'
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor='end'
              height={80}
              interval={0}
            />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} domain={[0, 'dataMax']} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '8px',
              }}
              formatter={(value: any) => [`${value}`, t('dashboard:total_users')]}
              labelFormatter={label => `${label}`}
            />
            <Area
              type='monotone'
              dataKey='value'
              stroke={CHART_COLORS.primary}
              strokeWidth={3}
              fill='url(#colorGradient)'
              fillOpacity={0.6}
              activeDot={{ r: 6 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
